import { useEffect, useState } from "react"

import { ScaleLoader } from "react-spinners"
import { formatNumber, Text, useMainContext } from "../../../../../modules/Dashboard"
import { useGetAllAssistant, useGetPredictHistory, useGetTickerDetails, useGetTickerDetailsForce } from "../../../services/usePredict"
import { FlexCenter, FlexColumn } from "../../../../../styles/CommonStyles"
import { SOCKET_URL } from "../../../../../core/constants/urls"
import { Link, useNavigate } from "react-router-dom"
import { usePredictContext } from "../../../context/PredictContextProvider"
import PairImg from "../../PairImg"
import { useQueryContext } from "../../../../../core/contexts/query"
import { formatDateShort } from "../../../../plans/components/CurrentUserPlan/helper"
import CalenderIcon from "../../../../../components/icons/CalenderIcon"
import XIcon from "../../../../../components/icons/XIcon"
import { FaEye, FaTimes } from "react-icons/fa"
import { FaCheckDouble, FaSpinner } from "react-icons/fa6"



const PredictHistoryWidget = () => {
    const {
        main: { lang },
    } = useMainContext()
    const { setToast } = useQueryContext()
    const { predictHistory, assistant, setTicker, setResponseType } = usePredictContext()
    const navigate = useNavigate()

    const [filterQueries] = useState({
        page: 1,
        dateFrom: null,
        search: {},
        query: {},
        limit: 6
    })
    const { data: assistants, isFetching: isFetchingAssistants } = useGetAllAssistant()
    const { data: history, isFetching: isFetchingHistory, refetch } = useGetPredictHistory(filterQueries)

    let loading = isFetchingAssistants || isFetchingHistory

    useEffect(() => {
        refetch()
    }, [refetch])


    const onClick = (data) => {
        if (isFailed(data)) {
            setToast({ show: true, message: "analyze-process-failed-err", isError: true })
            return
        }
        if (!data.isReady || !data.predictHistoryId) {
            setToast({ show: true, message: "analyze-not-ready-err", isError: true })
            return
        }
        const assistantData = assistants.find(a => a.value === data.assistant)

        predictHistory.setPredictHistoryId(data.predictHistoryId)
        assistant.setAssistant(assistantData)
        setTicker(data.ticker)
        setResponseType(data.responseType)
        navigate(`/assistant/${data.tickerCase}?step=result`)
    }

    return (
        <div className="h-full p-4 py-7">
            {/* Header */}
            <div className='relative flex justify-between gap-1 items-center pb-4'>
                <Text tid={"recent-analysis"} className='text-xl' />
                <Link to={"/transactions/predict"}>
                    <Text tid={"all-analysis"} className='underline text-mainColor cursor-pointer' />
                </Link>
            </div>

            {/* Loading */}
            {loading && (
                <div className="p-4 text-center text-sm text-gray-500">
                    <ScaleLoader size={18} color='#9F64DF' />
                </div>
            )}

            {/* Items */}
            {history?.data?.map((item) => (
                <div
                    key={item._id}
                    className="py-3 px-5 bg-[#1E1533] rounded-xl my-2 hover:scale-[1.02] transition-all duration-200 "

                >
                    <FlexColumn className="!w-full items-start gap-3">
                        <FlexCenter className="gap-1 w-full">
                            {/* Date */}
                            <CalenderIcon size={16} color="#fc6432" />
                            <span className="text-xs text-primaryOrange text-right font-medium">
                                {formatDateShort(item.createdAt, lang)}
                            </span>
                            {/* Status Button */}
                            <div className="ltr:ml-auto rtl:mr-auto">
                                <button
                                    onClick={() => onClick(item)}
                                    className={`px-2 md:px-4 py-2 rounded-lg md:rounded-xl hover:animate-pulse ${getStatusColor(item)}`}
                                >
                                    {getStatusLabel(item)}
                                </button>
                            </div>
                        </FlexCenter>
                        {/* Ticker */}
                        <FlexCenter className="gap-2 justify-start w-full">
                            <PairImg ticker={item.ticker} size={22} />
                            <span className="uppercase font-medium">{item.ticker}</span>
                        </FlexCenter>

                        {/* Assistant */}
                        <span className="text-xs text-gray-300 rounded-full px-2 py-1 border border-blue-400">
                            <Text tid={item.assistant} className="text-blue-400" />
                        </span>
                    </FlexColumn>
                </div>
            ))}

            {/* No data */}
            {!loading && !history?.data?.length ? (
                <div className='flex flex-col justify-start items-center h-full'>
                    <img
                        src={require("../../../../../assets/images/trading-pro/no-data.webp")}
                        width={100}
                        height={100}
                        alt="no-data"
                        className='my-5'
                    />
                    <Text tid={"list-is-empty"} />
                </div>
            ) : null}
        </div>
    )
}

export default PredictHistoryWidget


// ---- Helpers ----

// Returns `true` if analyze is failed (5 min passed)
const isFailed = (item) => {
    const created = new Date(item.createdAt).getTime()
    const now = Date.now()
    const diffMin = (now - created) / (1000 * 60)

    return diffMin >= 5 && !item.predictHistoryId
}

// Return status label with icon
export const getStatusLabel = (item) => {
    if (isFailed(item)) {
        return (
            <span className="flex items-center gap-1 ">
                <FaTimes size={14} />
                <Text tid={"failed"} className="font-medium text-sm md:text-base" />
            </span>
        )
    }

    if (item.isReady && !item.isRead) {
        return (
            <span className="flex items-center gap-1 ">
                <FaEye size={14} />
                <Text tid="view" className="font-medium text-sm md:text-base" />
            </span>
        )
    }

    if (item.isReady && item.isRead) {
        return (
            <span className="flex items-center gap-1 ">
                <FaCheckDouble size={14} />
                <Text tid="viewed" className="font-medium text-sm md:text-base" />
            </span>
        )
    }

    // Not ready + not read → Processing
    return (
        <span className="flex items-center gap-1 ">
            <FaSpinner size={14} />
            <Text tid={"processing"} className="font-medium text-sm md:text-base" />
        </span>
    )
}


// Returns tailwind color class for button background
export const getStatusColor = (item) => {
    if (isFailed(item)) return "bg-red-600"

    if (item.isReady && !item.isRead) return "bg-green-600"

    if (item.isReady && item.isRead) return "bg-blue-600"

    return "bg-yellow-600"
}
